/**
 * Add New Client form — industry-standard CPA fields.
 * Tabbed layout: Basic Info | Address | Tax Info | Firm Details.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
  Grid,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { enhancedApi, mapApiErrorsToForm } from '../api';
import { validators, runValidators } from '../utils/validators';
import { useFormValidation } from '../hooks/useFormValidation';
import { useDuplicateCheck } from '../hooks/useDuplicateCheck';
import { fieldHelperText } from '../utils/formHelpers';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const NAME_MAX_LENGTH = 100;
const NOTES_MAX_LENGTH = 1000;
const CLIENT_TYPES = ['Individual', 'Business', 'Trust', 'Estate', 'Partnership'];

const PREFERRED_CONTACT_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'sms', label: 'SMS' },
  { value: 'portal', label: 'Portal' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV',
  'WI', 'WY', 'DC',
];

const FILING_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'mfj', label: 'MFJ' },
  { value: 'mfs', label: 'MFS' },
  { value: 'hoh', label: 'HOH' },
];

const TAX_YEAR_END_OPTIONS = [
  { value: '12-31', label: 'Dec 31' },
  { value: 'custom', label: 'Custom' },
];

const SOURCE_OPTIONS = [
  { value: 'Referral', label: 'Referral' },
  { value: 'Website', label: 'Website' },
  { value: 'Cold', label: 'Cold' },
  { value: 'Event', label: 'Event' },
  { value: 'Other', label: 'Other' },
];

const PIPELINE_STAGE_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const SERVICE_TYPE_OPTIONS = ['Tax', 'Bookkeeping', 'Payroll', 'Advisory'];

const makeEmptyContactShell = () => ({
  first_name: '',
  last_name: '',
  display_name: '',
  branch: '',
  client_type: '',
  email: [{ email_type: 'Personal Email', email: '', is_default: true }],
  phone: [],
  address: [{}],
  birth_date: '',
  source: '',
  assigned_users: [],
  assigned_teams: [],
  tags: [],
  ssn_iitm: '',
  occupation: '',
  employer: '',
  client_owner: '',
  external_id: '',
  filing_status: '',
  is_active: true,
});

const formatSsnEin = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 9 && digits.length > 2) {
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  }
  if (digits.length > 9) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
  }
  return digits;
};

const formatUsMobile = (raw) => {
  let d = String(raw).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (!d.length) return '';
  if (d.length <= 3) return `+1 (${d}`;
  if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const TabPanel = ({ value, index, children }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const dialogPaperSx = {
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};

const availableHelper = (
  <Typography component="span" variant="caption" sx={{ color: 'success.main' }}>
    Available
  </Typography>
);

const ClientForm = ({ open, onAddClient = () => {}, onClose = () => {}, useDrawer = false }) => {
  const navigate = useNavigate();
  const { checking: dupChecking, results: dupResults, checkClientField, clearField, resetAll: resetDuplicateState } =
    useDuplicateCheck();
  const [tabValue, setTabValue] = useState(0);

  const [clientType, setClientType] = useState('Individual');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [preferredContact, setPreferredContact] = useState('email');

  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('USA');

  const [ssnEin, setSsnEin] = useState('');
  const [filingStatus, setFilingStatus] = useState('');
  const [taxYearEnd, setTaxYearEnd] = useState('12-31');
  const [taxYearEndCustom, setTaxYearEndCustom] = useState('');
  const [statesOfFiling, setStatesOfFiling] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);

  const [assignedStaff, setAssignedStaff] = useState([]);
  const [clientSince, setClientSince] = useState(null);
  const [source, setSource] = useState('');
  const [pipelineStage, setPipelineStage] = useState('lead');
  const [hourlyRate, setHourlyRate] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [sendPortalInvite, setSendPortalInvite] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');

  const clientSchema = useMemo(
    () => ({
      firstName: [validators.required, validators.minLength(2), validators.maxLength(NAME_MAX_LENGTH)],
      lastName: [validators.required, validators.minLength(2), validators.maxLength(NAME_MAX_LENGTH)],
      primaryEmail: [validators.required, validators.email],
      mobilePhone: [validators.phone],
      businessName:
        clientType === 'Individual'
          ? []
          : [validators.required, validators.minLength(2), validators.maxLength(200)],
      zip: [validators.zipCode],
      hourlyRate: [validators.minValue(0), validators.maxValue(10000)],
      internalNotes: [validators.maxLength(NOTES_MAX_LENGTH)],
    }),
    [clientType]
  );

  const {
    errors,
    touched,
    validateField,
    touchField,
    validateAll,
    clearErrors,
    mergeServerFieldErrors,
  } = useFormValidation(clientSchema);

  const [staffOptions, setStaffOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const clientFormValues = useMemo(
    () => ({
      firstName,
      lastName,
      primaryEmail,
      mobilePhone,
      businessName,
      zip,
      hourlyRate,
      internalNotes,
    }),
    [firstName, lastName, primaryEmail, mobilePhone, businessName, zip, hourlyRate, internalNotes]
  );

  const isFormValid = useCallback(() => {
    return !Object.keys(clientSchema).some((key) =>
      runValidators(clientFormValues[key], clientSchema[key])
    );
  }, [clientSchema, clientFormValues]);

  const handleSsnEinChange = (e) => {
    const formatted = formatSsnEin(e.target.value);
    setSsnEin(formatted);
    clearField('ssn');
    clearField('ein');
  };

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [usersRes, tagsRes] = await Promise.all([
          enhancedApi.getUsers(),
          enhancedApi.getTags(),
        ]);
        const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.results || [];
        setStaffOptions(
          users.map((u) => ({
            id: u.id,
            label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
          }))
        );
        const tagList = Array.isArray(tagsRes.data) ? tagsRes.data : tagsRes.data?.results || [];
        setTagOptions(
          tagList.map((t) => ({
            id: t.id,
            name: t.name || t.label,
          }))
        );
      } catch {
        setSnackbar({ open: true, message: 'Failed to load form data', severity: 'error' });
      }
    };
    load();
  }, [open]);

  const resetForm = useCallback(() => {
    setTabValue(0);
    setClientType('Individual');
    setFirstName('');
    setLastName('');
    setBusinessName('');
    setPrimaryEmail('');
    setMobilePhone('');
    setPreferredContact('email');
    setStreetAddress('');
    setCity('');
    setState('');
    setZip('');
    setCountry('USA');
    setSsnEin('');
    setFilingStatus('');
    setTaxYearEnd('12-31');
    setTaxYearEndCustom('');
    setStatesOfFiling([]);
    setServiceTypes([]);
    setAssignedStaff([]);
    setClientSince(null);
    setSource('');
    setPipelineStage('lead');
    setHourlyRate('');
    setTags([]);
    setTagInputValue('');
    setSendPortalInvite(false);
    setInternalNotes('');
    clearErrors();
    resetDuplicateState();
  }, [clearErrors, resetDuplicateState]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const buildPayload = useCallback(() => {
    const isIndividual = clientType === 'Individual';
    const person = `${firstName.trim()} ${lastName.trim()}`.trim();
    const dispName = isIndividual
      ? person || 'Unknown'
      : businessName.trim() || person || 'Unknown';

    const emails = [];
    if (primaryEmail?.trim()) {
      emails.push({ email: primaryEmail.trim(), email_type: 'Personal Email', is_default: true });
    }

    const phones = [];
    if (mobilePhone?.trim()) {
      phones.push({ phone: mobilePhone.trim(), phone_type: 'Mobile', is_default: true });
    }

    const addressEntry =
      streetAddress || city || state || zip || country
        ? { street1: streetAddress, city, state, zipcode: zip, country }
        : {};

    const taxYearEndValue =
      taxYearEnd === 'custom' && taxYearEndCustom.trim()
        ? taxYearEndCustom.trim()
        : taxYearEnd;

    const primaryContact = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      display_name: dispName,
      client_type: clientType,
      branch: '',
      ssn_iitm: ssnEin || '',
      occupation: '',
      employer: '',
      client_owner: '',
      external_id: '',
      filing_status: filingStatus || '',
      is_active: true,
      source: source || '',
      email: emails.length ? emails : [{ email_type: 'Personal Email', email: '', is_default: true }],
      phone: phones.length ? phones : [],
      address: Object.keys(addressEntry).length ? [addressEntry] : [{}],
      birth_date: '',
      assigned_users: assignedStaff.map((s) => (typeof s === 'object' ? s.id : s)),
      assigned_teams: [],
      tags: tags.map((t) => (typeof t === 'object' && t?.id != null ? t.id : t)).filter((x) => x != null && x !== ''),
      preferred_contact: preferredContact,
      client_since: clientSince ? dayjs(clientSince).format('YYYY-MM-DD') : '',
      referred_by: '',
      pipeline_stage: pipelineStage,
      hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
      send_portal_invite: sendPortalInvite,
      internal_notes: internalNotes?.trim() || '',
      states_of_filing: statesOfFiling,
      service_types: serviceTypes,
      tax_year_end: taxYearEndValue,
    };

    const emptyShell = makeEmptyContactShell();
    return {
      is_individual_client: isIndividual,
      is_business_client: !isIndividual,
      ind_contacts: {
        primary_contact: primaryContact,
        secondary_contact: { ...emptyShell },
        other_contact: { ...emptyShell },
      },
      assigned_teams: [],
    };
  }, [
    clientType,
    firstName,
    lastName,
    businessName,
    primaryEmail,
    mobilePhone,
    preferredContact,
    streetAddress,
    city,
    state,
    zip,
    country,
    ssnEin,
    filingStatus,
    taxYearEnd,
    taxYearEndCustom,
    statesOfFiling,
    serviceTypes,
    assignedStaff,
    clientSince,
    source,
    pipelineStage,
    hourlyRate,
    tags,
    sendPortalInvite,
    internalNotes,
  ]);

  const handleSubmit = async () => {
    if (!validateAll(clientFormValues)) {
      setSnackbar({ open: true, message: 'Please fix the errors before submitting', severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      const response = await enhancedApi.createClient(payload);
      const data = response.data;
      const clientId = data?.id ?? data?.client_id ?? data?.client?.id;
      onAddClient(response.data);
      clearErrors();
      resetForm();
      onClose();
      setSnackbar({ open: true, message: 'Client created', severity: 'success' });
      if (clientId) {
        navigate(`/client/${clientId}`);
      }
    } catch (err) {
      const raw = err.response?.data;
      const mapped = mapApiErrorsToForm(raw);
      const remap = { ...mapped };
      const emailErr = remap.email || remap.primary_email || remap['primary_contact.email'];
      if (emailErr) remap.primaryEmail = emailErr;
      if (remap.first_name) remap.firstName = remap.first_name;
      if (remap.last_name) remap.lastName = remap.last_name;
      if (Object.keys(remap).length) mergeServerFieldErrors(remap);
      const msg =
        raw?.error ||
        raw?.detail ||
        (typeof raw === 'object' && raw ? JSON.stringify(raw) : null) ||
        err.message ||
        'Failed to create client';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : 'Failed to create client', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && tagInputValue?.trim()) {
      e.preventDefault();
      const val = tagInputValue.trim();
      if (!tags.some((t) => (typeof t === 'object' ? t?.name : t) === val)) {
        const opt = tagOptions.find((t) => (t?.name || t?.label) === val) || { name: val };
        setTags((prev) => [...prev, opt]);
      }
      setTagInputValue('');
    }
  };

  const firstErr = touched.firstName && errors.firstName;
  const lastErr = touched.lastName && errors.lastName;
  const emailErr = touched.primaryEmail && errors.primaryEmail;
  const bizErr = touched.businessName && errors.businessName;
  const phoneErr = touched.mobilePhone && errors.mobilePhone;
  const zipErr = touched.zip && errors.zip;
  const rateErr = touched.hourlyRate && errors.hourlyRate;
  const notesErr = touched.internalNotes && errors.internalNotes;

  useEffect(() => {
    clearField('ssn');
    clearField('ein');
  }, [clientType, clearField]);

  const dupEmail = dupResults.email;
  const dupPhone = dupResults.phone;
  const dupCompany = dupResults.company_name;
  const taxDupKey = clientType === 'Individual' ? 'ssn' : 'ein';
  const dupTaxId = dupResults[taxDupKey];

  const formBody = (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 2,
          py: 2,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          Add New Client
        </Typography>
        <IconButton onClick={handleClose} aria-label="Close" disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flexShrink: 0,
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar - 1,
          }}
        >
          <Tab label="Basic Info" />
          <Tab label="Address" />
          <Tab label="Tax Info" />
          <Tab label="Firm Details" />
        </Tabs>

        <Box sx={{ overflow: 'auto', flex: 1, px: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Client Type
              </Typography>
              <ToggleButtonGroup
                value={clientType}
                exclusive
                onChange={(_, v) => v && setClientType(v)}
                size="small"
                sx={{
                  flexWrap: 'wrap',
                  gap: 0.5,
                  '& .MuiToggleButton-root': { typography: 'body2' },
                }}
              >
                {CLIENT_TYPES.map((t) => (
                  <ToggleButton key={t} value={t}>
                    {t}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  required
                  value={firstName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFirstName(v);
                    if (touched.firstName) validateField('firstName', v);
                  }}
                  onBlur={() => touchField('firstName', firstName)}
                  error={Boolean(firstErr)}
                  helperText={fieldHelperText(firstErr, NAME_MAX_LENGTH, firstName.length)}
                  disabled={submitting}
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                  InputProps={{
                    ...(firstErr && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <ErrorOutlineIcon color="error" />
                        </InputAdornment>
                      ),
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  required
                  value={lastName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLastName(v);
                    if (touched.lastName) validateField('lastName', v);
                  }}
                  onBlur={() => touchField('lastName', lastName)}
                  error={Boolean(lastErr)}
                  helperText={fieldHelperText(lastErr, NAME_MAX_LENGTH, lastName.length)}
                  disabled={submitting}
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                  InputProps={{
                    ...(lastErr && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <ErrorOutlineIcon color="error" />
                        </InputAdornment>
                      ),
                    }),
                  }}
                />
              </Grid>
              {clientType !== 'Individual' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    required
                    value={businessName}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBusinessName(v);
                      clearField('company_name');
                      if (touched.businessName) validateField('businessName', v);
                    }}
                    onBlur={() => {
                      touchField('businessName', businessName);
                      checkClientField('company_name', businessName, {
                        duplicateMessage: 'A client with this business name already exists',
                      });
                    }}
                    error={Boolean(bizErr || typeof dupCompany === 'string')}
                    helperText={
                      bizErr ||
                      (typeof dupCompany === 'string' ? dupCompany : dupCompany === 'ok' ? availableHelper : undefined)
                    }
                    disabled={submitting}
                    InputProps={{
                      endAdornment:
                        dupChecking.company_name ||
                        typeof dupCompany === 'string' ||
                        dupCompany === 'ok' ||
                        bizErr ? (
                          <InputAdornment position="end" sx={{ gap: 0.5 }}>
                            {dupChecking.company_name ? (
                              <CircularProgress size={16} />
                            ) : typeof dupCompany === 'string' ? (
                              <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                            ) : dupCompany === 'ok' ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                            ) : bizErr ? (
                              <ErrorOutlineIcon color="error" />
                            ) : null}
                          </InputAdornment>
                        ) : undefined,
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Primary Email"
                  type="email"
                  required
                  value={primaryEmail}
                  onChange={(ev) => {
                    const v = ev.target.value;
                    setPrimaryEmail(v);
                    clearField('email');
                    if (touched.primaryEmail) validateField('primaryEmail', v);
                  }}
                  onBlur={() => {
                    touchField('primaryEmail', primaryEmail);
                    checkClientField('email', primaryEmail, {
                      duplicateMessage: 'A client with this email already exists',
                      normalize: (val) => String(val ?? '').trim().toLowerCase(),
                    });
                  }}
                  error={Boolean(emailErr || typeof dupEmail === 'string')}
                  helperText={
                    emailErr
                      ? emailErr
                      : typeof dupEmail === 'string'
                        ? dupEmail
                        : dupEmail === 'ok'
                          ? availableHelper
                          : undefined
                  }
                  disabled={submitting}
                  InputProps={{
                    endAdornment:
                      dupChecking.email || typeof dupEmail === 'string' || dupEmail === 'ok' || emailErr ? (
                        <InputAdornment position="end" sx={{ gap: 0.5 }}>
                          {dupChecking.email ? (
                            <CircularProgress size={16} />
                          ) : typeof dupEmail === 'string' ? (
                            <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                          ) : dupEmail === 'ok' ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : emailErr ? (
                            <ErrorOutlineIcon color="error" />
                          ) : null}
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile Phone"
                  value={mobilePhone}
                  onChange={(e) => {
                    const v = formatUsMobile(e.target.value);
                    setMobilePhone(v);
                    clearField('phone');
                    if (touched.mobilePhone) validateField('mobilePhone', v);
                  }}
                  onBlur={() => {
                    touchField('mobilePhone', mobilePhone);
                    const digits = mobilePhone.replace(/\D/g, '');
                    const core = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
                    checkClientField('phone', core, {
                      minLength: 10,
                      duplicateMessage: 'A client with this phone number already exists',
                      normalize: (val) => String(val ?? '').replace(/\D/g, ''),
                    });
                  }}
                  error={Boolean(phoneErr || typeof dupPhone === 'string')}
                  helperText={
                    phoneErr
                      ? phoneErr
                      : typeof dupPhone === 'string'
                        ? dupPhone
                        : dupPhone === 'ok'
                          ? availableHelper
                          : '+1 format (E.164-style). Used for SMS.'
                  }
                  disabled={submitting}
                  placeholder="+1 (555) 123-4567"
                  InputProps={{
                    endAdornment:
                      dupChecking.phone || typeof dupPhone === 'string' || dupPhone === 'ok' || phoneErr ? (
                        <InputAdornment position="end" sx={{ gap: 0.5 }}>
                          {dupChecking.phone ? (
                            <CircularProgress size={16} />
                          ) : typeof dupPhone === 'string' ? (
                            <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                          ) : dupPhone === 'ok' ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : phoneErr ? (
                            <ErrorOutlineIcon color="error" />
                          ) : null}
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="preferred-contact-label">Preferred Contact</InputLabel>
                  <Select
                    labelId="preferred-contact-label"
                    value={preferredContact}
                    onChange={(e) => setPreferredContact(e.target.value)}
                    label="Preferred Contact"
                  >
                    {PREFERRED_CONTACT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street"
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="addr-state-label">State</InputLabel>
                  <Select
                    labelId="addr-state-label"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    label="State"
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {US_STATES.map((sCode) => (
                      <MenuItem key={sCode} value={sCode.trim()}>
                        {sCode.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ZIP"
                  value={zip}
                  onChange={(e) => {
                    const v = e.target.value;
                    setZip(v);
                    if (touched.zip) validateField('zip', v);
                  }}
                  onBlur={() => touchField('zip', zip)}
                  error={Boolean(zipErr)}
                  helperText={zipErr || undefined}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={submitting}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SSN / EIN"
                  type="password"
                  autoComplete="new-password"
                  value={ssnEin}
                  onChange={handleSsnEinChange}
                  onBlur={() => {
                    const digits = ssnEin.replace(/\D/g, '');
                    if (clientType === 'Individual') {
                      checkClientField('ssn', digits, {
                        minLength: 9,
                        duplicateMessage: 'A client with this SSN already exists',
                        normalize: (v) => String(v ?? '').replace(/\D/g, ''),
                      });
                    } else {
                      checkClientField('ein', digits, {
                        minLength: 9,
                        duplicateMessage: 'A client with this EIN already exists',
                        normalize: (v) => String(v ?? '').replace(/\D/g, ''),
                      });
                    }
                  }}
                  error={typeof dupTaxId === 'string'}
                  helperText={
                    typeof dupTaxId === 'string'
                      ? dupTaxId
                      : dupTaxId === 'ok'
                        ? availableHelper
                        : 'Masked entry. SSN ###-##-#### or EIN ##-#######'
                  }
                  disabled={submitting}
                  placeholder="•••••••••"
                  InputProps={{
                    endAdornment:
                      dupChecking[taxDupKey] || typeof dupTaxId === 'string' || dupTaxId === 'ok' ? (
                        <InputAdornment position="end" sx={{ gap: 0.5 }}>
                          {dupChecking[taxDupKey] ? (
                            <CircularProgress size={16} />
                          ) : typeof dupTaxId === 'string' ? (
                            <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                          ) : dupTaxId === 'ok' ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : null}
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="filing-status-label">Filing Status</InputLabel>
                  <Select
                    labelId="filing-status-label"
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                    label="Filing Status"
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {FILING_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="tax-ye-label">Tax Year End</InputLabel>
                  <Select
                    labelId="tax-ye-label"
                    value={taxYearEnd}
                    onChange={(e) => setTaxYearEnd(e.target.value)}
                    label="Tax Year End"
                  >
                    {TAX_YEAR_END_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {taxYearEnd === 'custom' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Custom year end (MM-DD)"
                    value={taxYearEndCustom}
                    onChange={(e) => setTaxYearEndCustom(e.target.value)}
                    disabled={submitting}
                    placeholder="06-30"
                    helperText="e.g. 06-30 for fiscal year"
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="states-filing-label">States of Filing</InputLabel>
                  <Select
                    labelId="states-filing-label"
                    multiple
                    value={statesOfFiling}
                    onChange={(e) => setStatesOfFiling(e.target.value)}
                    label="States of Filing"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((s) => (
                          <Chip key={s} label={s} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {US_STATES.map((sCode) => {
                      const code = sCode.trim();
                      return (
                        <MenuItem key={code} value={code}>
                          {code}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="svc-types-label">Service Types</InputLabel>
                  <Select
                    labelId="svc-types-label"
                    multiple
                    value={serviceTypes}
                    onChange={(e) => setServiceTypes(e.target.value)}
                    label="Service Types"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((s) => (
                          <Chip key={s} label={s} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {SERVICE_TYPE_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={staffOptions}
                  getOptionLabel={(opt) => (typeof opt === 'object' ? opt.label : '')}
                  value={assignedStaff}
                  onChange={(_, v) => setAssignedStaff(v)}
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField {...params} label="Assigned Staff" placeholder="Search staff" />
                  )}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? opt) === (val?.id ?? val)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Client Since"
                  value={clientSince}
                  onChange={(v) => setClientSince(v)}
                  slotProps={{
                    textField: { fullWidth: true, disabled: submitting },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="source-label">Source</InputLabel>
                  <Select
                    labelId="source-label"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    label="Source"
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {SOURCE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="pipeline-label">Pipeline Stage</InputLabel>
                  <Select
                    labelId="pipeline-label"
                    value={pipelineStage}
                    onChange={(e) => setPipelineStage(e.target.value)}
                    label="Pipeline Stage"
                  >
                    {PIPELINE_STAGE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hourly Rate ($)"
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setHourlyRate(v);
                    if (touched.hourlyRate) validateField('hourlyRate', v);
                  }}
                  onBlur={() => touchField('hourlyRate', hourlyRate)}
                  error={Boolean(rateErr)}
                  helperText={rateErr || undefined}
                  disabled={submitting}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tags"
                  placeholder="Type and press Enter"
                  value={tagInputValue}
                  onChange={(e) => setTagInputValue(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  disabled={submitting}
                  helperText="Add tags as chips (Enter to add)"
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {tags.map((t, i) => (
                    <Chip
                      key={i}
                      label={typeof t === 'object' ? t.name : t}
                      size="small"
                      onDelete={() => setTags((prev) => prev.filter((_, idx) => idx !== i))}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={sendPortalInvite}
                      onChange={(e) => setSendPortalInvite(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Send Portal Invite"
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Internal Notes"
                  value={internalNotes}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, NOTES_MAX_LENGTH);
                    setInternalNotes(v);
                    if (touched.internalNotes) validateField('internalNotes', v);
                  }}
                  onBlur={() => touchField('internalNotes', internalNotes)}
                  error={Boolean(notesErr)}
                  helperText={fieldHelperText(notesErr, NOTES_MAX_LENGTH, internalNotes.length)}
                  disabled={submitting}
                  inputProps={{ maxLength: NOTES_MAX_LENGTH }}
                />
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormValid() || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Create Client
        </Button>
      </DialogActions>
    </>
  );

  const snackbarEl = (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={snackbar.severity === 'error' ? 6000 : 4000}
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={snackbar.severity}
        variant="filled"
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  const localizedTree = useDrawer ? (
    <>
      {formBody}
      {snackbarEl}
    </>
  ) : (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        {formBody}
      </Dialog>
      {snackbarEl}
    </>
  );

  return <LocalizationProvider dateAdapter={AdapterDayjs}>{localizedTree}</LocalizationProvider>;
};

export default ClientForm;
